//@ts-nocheck
###
Model
=====

TypeScript is very against meta-programming. This is a base class that provides
the meta programming methods that I've used in whimsy.space. This is a prototype
to see how things would work with TypeScript in a class based approach.

The long term goal is to be able to get good type checking, docs, and intellisense.
Sadly it means working with more boilerplate code to make TS happier.

Status: Legacy/Experimental
###

// Models uses [Observable](/observable/docs) to keep the internal data in sync.

Observable from @danielx/observable

export class Model
  ###
  `I` holds the instance state. It is generally considered private, but access
  is available for debugging and other purposes.
  ###
  I
  constructor(@I={})

  ###
  Generates a public jQuery style getter / setter method for each `String` argument.

  >     #! example
  >     myObject = Model
  >       r: 255
  >       g: 0
  >       b: 100
  >
  >     myObject.attrAccessor "r", "g", "b"
  >
  >     myObject.r(254)
  ###
  attrAccessor = (attrNames...) =>
    attrNames.forEach (attrName) =>
      me := @
      @[attrName] = (newValue) ->
        if arguments.length > 0
          me.I[attrName] = newValue

          return me
        else
          me.I[attrName]

    return @

  ###
  Generates a public jQuery style getter method for each String argument.

  >     #! example
  >     myObject = Model
  >       r: 255
  >       g: 0
  >       b: 100
  >
  >     myObject.attrReader "r", "g", "b"
  >
  >     [myObject.r(), myObject.g(), myObject.b()]

  ###
  attrReader = (attrNames...) =>
    attrNames.forEach (attrName) =>
      @[attrName] = =>
        @I[attrName]

    return @

  ###
  Extends this object with methods from the passed in object. A shortcut for Object.extend(self, methods)

  >     I =
  >       x: 30
  >       y: 40
  >       maxSpeed: 5
  >
  >     # we are using extend to give player
  >     # additional methods that Model doesn't have
  >     player = Model(I).extend
  >       increaseSpeed: ->
  >         I.maxSpeed += 1
  >
  >     player.increaseSpeed()

  ###
  extend = (objects...) =>
    Object.assign @, objects...

  ###
  Includes a module in this object. A module is a constructor that takes two parameters, `I` and `self`

  >     myObject = Model()
  >     myObject.include(Bindable)

  >     # now you can bind handlers to functions
  >     myObject.bind "someEvent", ->
  >       alert("wow. that was easy.")
  ###
  include = (modules...) =>
    for Module of modules
      Module(@I, @)

    return @

  ###
  Bind a data model getter/setter to an attribute. The data model is bound directly to
  the attribute and must be directly convertible to and from JSON.
  ###
  attrData = (name, DataModel) =>
    @I[name] = DataModel(@I[name])

    Object.defineProperty @, name,
      get: =>
        @[name]
      set: (value) =>
        @[name] = DataModel(value)

  ###
  Observe any number of attributes as observables. For each attribute name passed in we expose a public getter/setter method and listen to changes when the value is set.
  ###
  attrObservable = (names...) =>
    names.forEach (name) =>
      @[name] = Observable(@I[name])

      @[name].observe (newValue) =>
        @I[name] = newValue

    return @

  // Experimental, observe with a writer function
  observable = (name, writer) =>
    o := Observable writer @I[name]

    o.observe (newValue) =>
      @I[name] = writer newValue

    @[name] = (v) ->
      if arguments.length > 0
        o writer v
      else
        o()

  ###
  Observe an attribute as a model. Treats the attribute given as an Observable
  model instance exposing a getter/setter method of the same name. The Model
  constructor must be passed explicitly.
  ###
  attrModel = (name, Model) =>
    model := Model(@I[name])

    @[name] = Observable(model)

    @[name].observe (newValue) =>
      @I[name] = newValue.I

    return @

  ###
  Observe an attribute as an array of sub-models. This is the same as `attrModel`
  except the attribute is expected to be an array of models rather than a single one.
  ###
  attrModels = (name, Model) =>
    models := (@I[name] or []).map (x) =>
      Model(x)

    @[name] = Observable(models)

    @[name].observe (newValue) =>
      @I[name] = newValue.map (instance) =>
        instance.I

    return @

  ###
  Delegate methods to another target. Makes it easier to compose rather than extend.
  ###
  delegate = (names..., {to}) =>
    names.forEach (name) =>
      Object.defineProperty @, name,
        get: =>
          receiver := getValue @, to
          receiver[name]
        set: (value) =>
          receiver := getValue @, to
          setValue receiver, name, value

  ###
  The JSON representation is kept up to date via the observable properites and resides in `I`.
  ###
  toJSON = =>
    @I

isFn := (x: unknown): x is Function ->
  typeof x is 'function'

getValue := <R, P extends keyof R>(receiver: R, property: P) ->
  base :=receiver[property]
  if isFn base
    base()
  else
    receiver[property]

setValue := <R, P extends keyof R, T extends R[P]>(receiver: R, property: P, value: T) ->
  target := receiver[property]

  if isFn target
    target.call(receiver, value)
  else
    receiver[property] = value

export extend := Object.assign

export default Model
